import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  BuyerSupplierAwardsOverTimeQuery,
  BuyerSupplierAwardsOverTimeRequest_IntervalEnum_0 as AwardsInterval,
} from "lib/generated/app-service-gql/graphql";
import { SupplierMentionType } from "lib/types/graphQLEnums";
import { useGraphQlQuery } from "../useGraphQlClient";

const buyerSupplierAwardsOverTimeQuery = graphql(`
  query buyerSupplierAwardsOverTime($request: BuyerSupplierAwardsOverTimeRequest!) {
    buyerSupplierAwardsOverTime(BuyerSupplierAwardsOverTimeRequest: $request) {
      points {
        period
        awardCount
        awardValue
      }
    }
  }
`);

const INTERVAL: Record<Args["interval"], AwardsInterval> = {
  month: AwardsInterval.Month,
  quarter: AwardsInterval.Quarter,
  year: AwardsInterval.Year,
};

type Args = {
  buyerGuid: string;
  supplierGuid: string;
  interval: "month" | "quarter" | "year";
  publishedFrom?: string;
  supplierMentionType: SupplierMentionType;
};

/**
 * The pair's awards per period under the page's mention type, valued the same way as the headline
 * award value so the periods add up to it.
 */
export function useBuyerSupplierAwardsOverTime(
  { buyerGuid, supplierGuid, interval, publishedFrom, supplierMentionType }: Args,
  options?: UseQueryOptions<
    BuyerSupplierAwardsOverTimeQuery,
    unknown,
    BuyerSupplierAwardsOverTimeQuery,
    QueryKey
  >,
) {
  const { data, ...rest } = useGraphQlQuery(
    [
      "buyerSupplierAwardsOverTime",
      buyerGuid,
      supplierGuid,
      interval,
      publishedFrom,
      supplierMentionType,
    ],
    buyerSupplierAwardsOverTimeQuery,
    [
      {
        request: {
          buyerGuid,
          supplierGuid,
          interval: INTERVAL[interval],
          publishedFrom,
          supplierMentionType,
        },
      },
    ],
    options,
  );

  return { data: data?.buyerSupplierAwardsOverTime, ...rest };
}
